// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Mocks
import { mockedActions } from "@/Dashboard/mock/mockedActions";

// Types
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IOnlineRatings, IReviewData } from "@DataPages/types/socialMediaReviews/model";
import { ICommissionCycleTopSalesResponse, ICSIPerformanceMetricsResponse, IUsersByMonthOfBirthDayResponse } from "../types/queries";

export async function getCSIPerformanceMetrics(locationId: string) {
	const query = gql`
		query getCSIPerformanceMetrics($locationId: String!) {
			getCSIPerformanceMetrics(locationId: $locationId) {
				franchise
				salesMetrics {
					amt
					dealership
					key
					name
					region
					you
				}
				serviceMetrics {
					amt
					dealership
					key
					name
					region
					you
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getCSIPerformanceMetrics", ICSIPerformanceMetricsResponse[]> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getCSIPerformanceMetrics;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getCommissionCycleTopSales(locationId: string, date: string) {
	const query = gql`
		query getTopSalesAssociate($locationId: String!, $date: AWSDateTime!) {
			getTopSalesAssociate(locationId: $locationId, date: $date) {
				_id
				title
				topSalesAssociate {
					_id
					fullName
					units
				}
			}
		}
	`;

	const variables = {
		locationId,
		date,
	};

	try {
		const response: GraphqlQueryResponse<"getTopSalesAssociate", ICommissionCycleTopSalesResponse> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getTopSalesAssociate;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getSocialMediaReviews(locationId: string, commissionCycleId: string) {
	const query = gql`
		query getSocialMediaReviews($locationId: String!, $commissionCycleId: String!) {
			getSocialMediaReviews(locationId: $locationId, commissionCycleId: $commissionCycleId) {
				_id
				locationId
				storeReviews {
					monthToDate {
						averageRating
						reviewCount
						siteName
						neededResponseTotal
					}
					lastMonth {
						averageRating
						reviewCount
						siteName
					}
					lastThreeMonths {
						averageRating
						reviewCount
						siteName
					}
					yearToDate {
						averageRating
						reviewCount
						siteName
					}

					allTime {
						averageRating
						reviewCount
						siteName
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
	};

	try {
		const response: GraphqlQueryResponse<
			"getSocialMediaReviews",
			Pick<IReviewData, "_id" | "locationId" | "storeReviews">
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSocialMediaReviews;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getSocialMediaStoreReviews",
			message: "Failed to fetch social media store reviews",
			variables,
		});
		return null;
	}
}

export async function getUsersByMonthOfBirthDay(locationId: string) {
	const query = gql`
		query getUsers($locationId: String!, $filter: UserFilterInput) {
			getUsers(locationId: $locationId, filter: $filter) {
				_id
				birthDate
				fullName
			}
		}
	`;

	const variables = {
		locationId,
		filter: {
			locationId,
			birthdays: true,
			status: "ACTIVE",
		},
	};

	try {
		const response: GraphqlQueryResponse<"getUsers", IUsersByMonthOfBirthDayResponse[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getUsers;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export function getUrgentActions(_locationId: string) {
	return mockedActions;
}

export async function removeUrgentAction(actionId: number) {
	return actionId;
}

export async function getOnlineRatings(locationId: string, filter: { store?: boolean; userIds?: string[] }) {
	const query = gql`
		query getOnlineRatings($locationId: String!, $filter: OnlineRatingsFilterInput) {
			getOnlineRatings(locationId: $locationId, filter: $filter) {
				_id
				locationId
				storeReviews {
					monthToDate {
						averageRating
						reviewCount
						siteName
					}
					lastMonth {
						averageRating
						reviewCount
						siteName
					}
					lastThreeMonths {
						averageRating
						reviewCount
						siteName
					}
					yearToDate {
						averageRating
						reviewCount
						siteName
					}
					allTime {
						averageRating
						reviewCount
						siteName
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		filter,
	};

	try {
		const response: GraphqlQueryResponse<"getOnlineRatings", IOnlineRatings> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getOnlineRatings;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getOnlineRatings",
			message: "Failed to fetch online ratings",
			variables,
		});
		return null;
	}
}
